import {Box} from '@dropbox/dig-foundations';
import {growthbookCacheAtom} from 'atoms/growthbook';
import {Avatar} from 'components/DSYS/Avatar';
import {Layout} from 'components/DSYS/Layout';
import {StatusButtonIcon} from 'components/DSYS/StatusButtonIcon';
import {useExclusiveHover} from 'components/DSYS/useExclusiveHover';
import {t} from 'i18next';
import {useAtomValue} from 'jotai';
import {useEffect, useMemo, useRef, useState} from 'react';
import {getWorkstreamUpdateVisualStatus} from 'views/workstreams/utils';

import {InlineToggletip} from './InlineToggletip';
import {NarrativeLinkIcon} from './NarrativeLinkIcon';
import {NestedNarrativesHint} from './NestedNarrativesHint';
import {PinToggleButton} from './PinToggleButton';
import {RailBadge} from './RailBadge';
import {TreeItem, WsNode} from './treeTypes';
import {logWorkstreamsInteraction} from './workstreamsAnalytics';

export type TreeSharedProps = {
  selectedId?: number;
  // Currently-selected pillar group (by name).
  selectedPillar?: string | null;
  onOpenDetails: (node: WsNode) => void;
  // Clicking a pillar-group header body (not its chevron) selects the pillar and
  // loads its cards on the right. The chevron alone expands/collapses.
  onOpenGroup?: (node: WsNode) => void;
  // Recents: a trailing pin toggle on top-level rows — filled while pinned,
  // outline on hover otherwise.
  onPinItem?: (node: WsNode) => void;
  isItemPinned?: (node: WsNode) => boolean;
  // Narrative doc links keyed by pillar name (shown when group is expanded).
  narrativeLinks?: Record<string, {text: string; url: string}>;
  // 'bold' marks a selected group header with bold text only (no row fill), so
  // it doesn't stack on the open group's tint. Defaults to 'fill'.
  groupSelectionStyle?: 'fill' | 'bold';
  // Lets the open group's tint run edge to edge: the rail's horizontal padding,
  // which the tinted root cancels with negative margins and restores inside.
  rootBleed?: {left: number; right: number};
  // Rows that can't expand keep the chevron's space when they have a link or
  // pin, so those icons form one column (the pillars; the top rows have no
  // chevrons, so theirs sit at the edge).
  reserveChevronSlot?: boolean;
  // Narrow screens' sheet: 44px rows and chevron tap targets.
  touch?: boolean;
  // Skip the tint behind the open root group (selection fills still show).
  disableRootTint?: boolean;
  // Groups with nothing of their own to show (e.g. the Enablement bucket):
  // clicking the row just shows / hides its children.
  isToggleOnlyGroup?: (node: WsNode) => boolean;
  // The URL a workstream row exposes (cmd-click / new tab). Defaults to the
  // full /workstreams/:id page.
  rowHref?: (node: WsNode) => string;
};

// A 12px dot + 8px gap, so a nested workstream's dot sits under its parent's name.
const DOT_NAME_GAP = 8;
const DOT_INDENT_PER_LEVEL = 12 + DOT_NAME_GAP;

type TreeRowProps = {
  item: TreeItem;
  hasChildren: boolean;
  expanded: boolean;
  shared: TreeSharedProps;
  onToggle: () => void;
  onExpand: () => void;
  narrativeLink?: {text: string; url: string} | null;
  // The workstream's linked roadmap doc, after its narrative.
  roadmapLink?: {text: string; url: string} | null;
  // A collapsed group without its own link: how many workstreams under it have one.
  nestedNarrativeCount?: number;
  // Nesting depth counting workstream parents (group headers don't add depth,
  // so their rows sit flush with the header).
  dotDepth: number;
};

// A rail row: status dot + name, the chevron in a trailing column. Clicking a
// workstream selects it; clicking a pillar group selects it and opens it. The
// row itself is Layout's RailTreeRow.
const TreeRow = ({
  item,
  hasChildren,
  expanded,
  shared,
  onToggle,
  onExpand,
  narrativeLink,
  roadmapLink,
  nestedNarrativeCount = 0,
  dotDepth,
}: TreeRowProps) => {
  const {node, level} = item;
  const {selectedId, onOpenDetails, onOpenGroup, onPinItem, isItemPinned} = shared;
  const isGroup = Boolean(node.isPillarGroup);
  // One hovered row page-wide (see useExclusiveHover); shared with the row, so
  // the pin shows on hover.
  const hover = useExclusiveHover();
  const {hovered} = hover;
  const groupSelected =
    isGroup &&
    shared.selectedPillar != null &&
    (node.pillar === shared.selectedPillar || node.name === shared.selectedPillar);
  const selected = selectedId === node.id || groupSelected;

  const pinned = isItemPinned?.(node) ?? false;
  const pinnable = Boolean(onPinItem) && level === 1;
  const pinButton =
    onPinItem && level === 1 && (pinned || hovered) ? (
      <PinToggleButton pinned={pinned} onToggle={() => onPinItem(node)} />
    ) : null;

  // Which row a link was opened from: a group's id is synthetic, so it's named by pillar.
  const linkLogProps = isGroup ? {pillar: node.pillar ?? node.name} : {workstream_id: node.id};
  const links =
    narrativeLink || roadmapLink ? (
      <>
        {narrativeLink && (
          <NarrativeLinkIcon link={narrativeLink} insideLink={!isGroup} logProps={linkLogProps} />
        )}
        {roadmapLink && (
          <NarrativeLinkIcon
            link={roadmapLink}
            insideLink={!isGroup}
            openedEvent="roadmap_link_opened"
            logProps={{...linkLogProps, from: 'rail'}}
          />
        )}
      </>
    ) : null;

  const trailingAction = pinButton ?? links;
  // A row that can't expand but has a link or pin keeps an invisible chevron,
  // so its icon lines up with the expandable rows'.
  const reserveChevron = !hasChildren && Boolean(shared.reserveChevronSlot && trailingAction);

  const trailingControl = links ? (
    <>
      {links}
      {/* Recents: the pin comes and goes on hover, after the links; its slot
          stays so they don't shift. */}
      {pinnable && (pinButton ?? <Box style={{width: 24, flexShrink: 0}} />)}
    </>
  ) : pinButton ? (
    pinButton
  ) : isGroup && item.badge ? (
    <RailBadge count={item.badge} />
  ) : nestedNarrativeCount > 0 ? (
    <NestedNarrativesHint count={nestedNarrativeCount} onExpand={onExpand} />
  ) : null;

  const leading = isGroup ? undefined : node.person ? (
    // Centered on the dot's 12px, so its name lines up with the workstreams'.
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      style={{flexShrink: 0, width: 12}}
    >
      <Avatar size="xsmall" user={node.person} />
    </Box>
  ) : (
    <Box display="flex" alignItems="center" style={{flexShrink: 0}}>
      <StatusButtonIcon
        status={getWorkstreamUpdateVisualStatus(node.latestStatus)}
        size="xsmall"
        includeArchived
      />
    </Box>
  );

  return (
    <Layout.RailTreeRow
      label={node.name}
      variant={isGroup ? 'group' : 'item'}
      // Every row starts with its dot (or a group's name) at a per-level indent of
      // the dot plus its gap, which puts a child's dot right under its parent's name.
      indentPx={dotDepth * DOT_INDENT_PER_LEVEL}
      selected={selected}
      selectedStyle={isGroup && shared.groupSelectionStyle === 'bold' ? 'bold' : 'fill'}
      leading={leading}
      leadingGap={isGroup ? 4 : DOT_NAME_GAP}
      labelAccessory={
        isGroup && item.tooltip ? (
          <InlineToggletip title={item.tooltip} placement="right" />
        ) : undefined
      }
      trailing={trailingControl}
      expandable={hasChildren}
      expanded={expanded}
      onToggle={() => {
        logWorkstreamsInteraction('rail_group_toggled', {expanded: !expanded});
        onToggle();
      }}
      reserveChevron={reserveChevron}
      // Real workstream rows are links, so the browser exposes the URL (shared.rowHref,
      // else the full detail page) for cmd-click / new tab; a plain click selects in the page.
      href={isGroup ? undefined : (shared.rowHref?.(node) ?? `/workstreams/${node.id}`)}
      onActivate={() => {
        if (!isGroup) {
          onOpenDetails(node);
          if (hasChildren) onExpand();
        } else if (shared.isToggleOnlyGroup?.(node)) {
          logWorkstreamsInteraction('rail_group_toggled', {expanded: !expanded, method: 'row'});
          onToggle();
        } else if (onOpenGroup) {
          // Pillar headers: clicking the body selects the group (its cards on the
          // right) and opens it; the chevron alone expands / collapses.
          onOpenGroup(node);
          onExpand();
        } else {
          onToggle();
        }
      }}
      bleed={shared.rootBleed}
      touch={shared.touch}
      hover={hover}
    />
  );
};

const treeContainsId = (item: TreeItem, id: number): boolean => {
  if (item.node.id === id) return true;
  return item.children.some((child) => treeContainsId(child, id));
};

const treeContainsGroup = (item: TreeItem, groupName: string): boolean => {
  if (item.node.isPillarGroup && (item.node.pillar === groupName || item.node.name === groupName)) {
    return true;
  }
  return item.children.some((child) => treeContainsGroup(child, groupName));
};

// Node ids from `item` down to the first row that matches, or null.
const findPath = (item: TreeItem, matches: (item: TreeItem) => boolean): number[] | null => {
  if (matches(item)) return [item.node.id];
  for (const child of item.children) {
    const path = findPath(child, matches);
    if (path) return [item.node.id, ...path];
  }
  return null;
};

// Opens the rows on `path` (this row first). `key` names the selection, so
// selecting something else opens again even after the user folded a row.
type Reveal = {key: string; path: number[]};

// Workstreams under a group that carry their own narrative link (each counted
// once, though a workstream can sit under several parents).
const countNestedNarratives = (item: TreeItem): number => {
  const ids = new Set<number>();
  const visit = (current: TreeItem) => {
    for (const child of current.children) {
      if (!child.node.isPillarGroup && child.node.narrativeUrl) ids.add(child.node.id);
      visit(child);
    }
  };
  visit(item);
  return ids.size;
};

const TreeNodeView = ({
  item,
  shared,
  dotDepth = 0,
  expandSignal = 0,
  reveal,
}: {
  item: TreeItem;
  shared: TreeSharedProps;
  dotDepth?: number;
  // Bumped by the parent to open this row (see soleChildExpandSignal).
  expandSignal?: number;
  // Set while this row is on the way to the selection.
  reveal?: Reveal;
}) => {
  const hasChildren = item.children.length > 0;
  // Opening on the selection at mount too (not in an effect), so a refresh
  // doesn't paint the rail folded first.
  const [expanded, setExpanded] = useState(Boolean(item.autoExpand || reveal));
  useEffect(() => {
    if (reveal) setExpanded(true);
    // Only a new selection opens it; the user can fold it again after.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reveal?.key]);
  const revealChildId = reveal?.path[1];
  // Opening a pillar whose only row is a workstream opens that one too (the
  // page selects it). Pillars only: a recent person's group has no pillar.
  const [soleChildExpandSignal, setSoleChildExpandSignal] = useState(0);
  const soleWorkstreamChild =
    Boolean(item.node.isPillarGroup && item.node.pillar) &&
    item.children.length === 1 &&
    !item.children[0].node.isPillarGroup;
  useEffect(() => {
    if (expandSignal > 0) setExpanded(true);
  }, [expandSignal]);
  // A pillar's own narrative; a merged one without one keeps its
  // workstream's (narrativeUrl, carried over), named after it.
  const narrativeLink = item.node.isPillarGroup
    ? (shared.narrativeLinks?.[item.node.pillar ?? item.node.name] ??
      (item.node.narrativeUrl
        ? {text: `${item.node.name} Narrative`, url: item.node.narrativeUrl}
        : null))
    : item.node.narrativeUrl
      ? {text: `${item.node.name} Narrative`, url: item.node.narrativeUrl}
      : null;
  const {isWorkstreamProductRoadmapEnabled} = useAtomValue(growthbookCacheAtom);
  const roadmapLink =
    isWorkstreamProductRoadmapEnabled && item.node.roadmapUrl
      ? {text: t('roadmap'), url: item.node.roadmapUrl}
      : null;
  const nestedNarrativeCount = useMemo(
    () => (item.node.isPillarGroup && item.node.pillar ? countNestedNarratives(item) : 0),
    [item]
  );
  const children = item.children.map((child) => (
    <TreeNodeView
      key={`${item.node.id}-${child.node.id}`}
      item={child}
      shared={shared}
      expandSignal={soleWorkstreamChild ? soleChildExpandSignal : 0}
      reveal={
        reveal && child.node.id === revealChildId
          ? {key: reveal.key, path: reveal.path.slice(1)}
          : undefined
      }
      // A group in a group lines up with it, unless it's nested (a step in).
      dotDepth={
        item.node.isPillarGroup && child.node.isPillarGroup && !child.nestedGroup
          ? dotDepth
          : dotDepth + 1
      }
    />
  ));

  return (
    <Box>
      <TreeRow
        item={item}
        hasChildren={hasChildren}
        expanded={expanded}
        shared={shared}
        onToggle={() => setExpanded((value) => !value)}
        onExpand={() => {
          setExpanded(true);
          if (soleWorkstreamChild) setSoleChildExpandSignal((value) => value + 1);
        }}
        narrativeLink={narrativeLink}
        roadmapLink={roadmapLink}
        nestedNarrativeCount={!expanded && !narrativeLink ? nestedNarrativeCount : 0}
        dotDepth={dotDepth}
      />
      {expanded && hasChildren && children}
    </Box>
  );
};

export const WorkstreamTree = ({roots, shared}: {roots: TreeItem[]; shared: TreeSharedProps}) => {
  const clickedRootRef = useRef<number | undefined>(undefined);

  const highlightedRootId = useMemo(() => {
    if (shared.selectedId !== undefined) {
      // If the user clicked within a specific root, prefer that one (handles duplicates).
      if (
        clickedRootRef.current !== undefined &&
        roots.some(
          (r) => r.node.id === clickedRootRef.current && treeContainsId(r, shared.selectedId!)
        )
      ) {
        return clickedRootRef.current;
      }
      // Fallback: first root that contains the selected ID.
      return roots.find(
        (item) => item.children.length > 0 && treeContainsId(item, shared.selectedId!)
      )?.node.id;
    }
    if (shared.selectedPillar) {
      if (
        clickedRootRef.current !== undefined &&
        roots.some(
          (r) =>
            r.node.id === clickedRootRef.current && treeContainsGroup(r, shared.selectedPillar!)
        )
      ) {
        return clickedRootRef.current;
      }
      return roots.find(
        (item) => item.children.length > 0 && treeContainsGroup(item, shared.selectedPillar!)
      )?.node.id;
    }
    return undefined;
  }, [roots, shared.selectedId, shared.selectedPillar]);

  // Whatever opened the selection (a card, search, a recent, a refresh), the
  // rail opens down to it inside the highlighted root. A pick in this tree is
  // already showing, so it's left alone (the first path could be another parent).
  const selectionKey =
    shared.selectedId !== undefined
      ? `workstream:${shared.selectedId}`
      : shared.selectedPillar
        ? `group:${shared.selectedPillar}`
        : null;
  const railPickRef = useRef<string | null>(null);
  const reveal = useMemo<Reveal | undefined>(() => {
    if (selectionKey === null || selectionKey === railPickRef.current) return undefined;
    const root = roots.find((item) => item.node.id === highlightedRootId);
    const path =
      root &&
      findPath(root, ({node}) =>
        shared.selectedId !== undefined
          ? !node.isPillarGroup && node.id === shared.selectedId
          : Boolean(node.isPillarGroup) &&
            (node.pillar === shared.selectedPillar || node.name === shared.selectedPillar)
      );
    return path ? {key: selectionKey, path} : undefined;
  }, [roots, highlightedRootId, selectionKey, shared.selectedId, shared.selectedPillar]);

  return (
    <Box style={{maxWidth: 1120}}>
      {roots.map((item) => {
        const rootShared =
          item.children.length > 0
            ? {
                ...shared,
                onOpenDetails: (node: WsNode) => {
                  clickedRootRef.current = item.node.id;
                  railPickRef.current = `workstream:${node.id}`;
                  shared.onOpenDetails(node);
                },
                onOpenGroup: shared.onOpenGroup
                  ? (node: WsNode) => {
                      clickedRootRef.current = item.node.id;
                      railPickRef.current = `group:${node.pillar ?? node.name}`;
                      shared.onOpenGroup!(node);
                    }
                  : undefined,
              }
            : shared;

        return (
          <Box
            key={item.node.id}
            borderRadius={shared.rootBleed ? undefined : 'Medium'}
            backgroundColor={
              // Toggle-only groups (the Enablement bucket) aren't a scope you
              // open, so they never take the tint, even around a selected row.
              !shared.disableRootTint &&
              item.node.id === highlightedRootId &&
              !shared.isToggleOnlyGroup?.(item.node)
                ? 'Opacity Surface'
                : undefined
            }
            style={
              shared.rootBleed
                ? {marginLeft: -shared.rootBleed.left, marginRight: -shared.rootBleed.right}
                : undefined
            }
          >
            <TreeNodeView
              item={item}
              shared={rootShared}
              reveal={reveal && reveal.path[0] === item.node.id ? reveal : undefined}
            />
          </Box>
        );
      })}
    </Box>
  );
};
